import Spacer from '@/common/components/spacer';
import { Link } from 'react-router';
import StatusBanner from '@/common/components/status-banner';
import { useMemo } from 'react';
import { InsurancePlan } from '@/catalog/types';
import { getInsurancePlanDeductibles } from '@/account/utils/get-insurance-plan-deductibles';
import { useDisableSpaNavigation } from '@/util-react-router/hooks/use-disable-spa-navigation';

export interface InsuranceDetailsContentProps {
  variant?: 'swap' | 'change';
  enableInsuranceChange?: boolean;
  insurancePlans?: InsurancePlan[];
}

export default function InsuranceDetailsContent({
  variant,
  enableInsuranceChange,
  insurancePlans,
}: InsuranceDetailsContentProps) {
  const bannerCopy = useMemo(() => {
    if (variant === 'change') {
      return {
        title: "We've got your back",
        message:
          'At Flexcar, selection of one of our coverage options is required to keep you protected on the road. We are unable to waive coverage because any personal auto insurance that you carry may not apply to Flexcar.',
      };
    }
    if (variant === 'swap') {
      return {
        title: 'Heads up!',
        message:
          'Flexcar has changed the way pricing is structured. Rest assured, this coverage is identical to the coverage you have today.',
      };
    }
    return {
      title: 'Does my existing car insurance / credit card cover my Flexcar?',
      message:
        "No, they do not. At Flexcar, basic liability insurance is always included, and selecting one of our protection plans is required. Supplementary coverage from existing personal auto insurance and credit card auto insurance only cover short-term use cases like rental cars. They do not generally cover your Flexcar vehicle, which is a longer-term car ownership replacement. Don't worry — we've got your back.",
    };
  }, [variant]);

  const { essentialPlanDeductible, enhancedPlanDeductible } =
    getInsurancePlanDeductibles(insurancePlans);

  const { disableSpaNavigation } = useDisableSpaNavigation();

  return (
    <>
      <div>
        <StatusBanner
          variant="info"
          title={bannerCopy.title}
          message={{
            text: bannerCopy.message,
          }}
        />
      </div>
      <Spacer variant="height24" />
      <p className="body-strong">Basic Liability Coverage</p>
      <Spacer variant="height8" />
      <p className="body">
        State Minimum liability coverage is provided in the state the incident occurs,
        included with every Flexcar subscription. You can review the state minimum
        coverage levels at publicly available resources online such as{' '}
        <Link
          to="https://www.nerdwallet.com/article/insurance/minimum-car-insurance-requirements"
          target="_blank"
          rel="noreferrer"
          className="link-body"
        >
          this
        </Link>
        .
      </p>
      <Spacer variant="height24" />
      <p className="body-strong">Damage Deductible</p>
      <Spacer variant="height8" />
      <p className="body">
        Flexcar customers owe only up to the damage deductible amount per incident for
        damage to the Flexcar. Flexcar covers the rest.
      </p>
      <ul>
        <li>With Essential Protection, the deductible is {essentialPlanDeductible}.</li>
        <li>
          With Enhanced Protection, the deductible is reduced to {enhancedPlanDeductible}{' '}
          and glass damage is fully covered with no deductible.
        </li>
      </ul>
      <Spacer variant="height24" />
      <p className="sub">
        {`Note: Once your order is placed, ${
          enableInsuranceChange
            ? `you can change your protection plan before your
          pickup or delivery. If you select the Enhanced plan, you can downgrade to
          Essential at any time after pickup as well`
            : `your protection plan cannot be changed`
        }. All coverage is dependent on full
          compliance with the `}
        <Link
          to="/membershipagreement"
          reloadDocument={disableSpaNavigation}
          className="sub underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Flexcar Membership Agreement.
        </Link>
      </p>
    </>
  );
}
